import React from "react";
import { NavLink } from "react-router-dom";
import "./module-table.scss";

export const statusSlug = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/\s+/g, "-");

const ModuleTable = ({
  title,
  description,
  count = null,
  columns = null,
  columnTemplate,
  variant,
  children,
  empty = null,
  footer = null,
  spaced = true,
  className = "",
}) => {
  const rootClass = [
    "module-table",
    spaced ? "module-table--spaced" : "module-table--flush",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const bodyClass = [
    "module-table__body",
    variant ? `module-table__body--${variant}` : "",
  ]
    .filter(Boolean)
    .join(" ");

  const headClass = [
    "module-table__head",
    variant === "merchants" ? "module-table__head--merchants" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <section className={rootClass}>
      {(title || description || count != null) ? (
        <div className="module-table__header">
          <div>
            {title ? <h3>{title}</h3> : null}
            {description ? <p>{description}</p> : null}
          </div>
          {count != null ? <span className="module-table__count">{count}</span> : null}
        </div>
      ) : null}

      <div className="module-table__wrap">
        <div className={bodyClass}>
          {columns ? (
            <div
              className={headClass}
              style={
                variant === "merchants" || !columnTemplate
                  ? undefined
                  : { gridTemplateColumns: columnTemplate }
              }
            >
              {columns.map((column) => (
                <span key={column}>{column}</span>
              ))}
            </div>
          ) : null}

          {children}

          {empty ? <div className="module-table__empty">{empty}</div> : null}
        </div>
      </div>

      {footer ? <div className="module-table__footer">{footer}</div> : null}
    </section>
  );
};

const ModuleTableRow = ({
  children,
  clickable = false,
  onClick,
  columnTemplate,
  className = "",
  as,
  type = "button",
  ...props
}) => {
  const Tag = as || (clickable || onClick ? "button" : "div");
  const classes = [
    "module-table__row",
    clickable || onClick ? "module-table__row--clickable" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <Tag
      type={Tag === "button" ? type : undefined}
      className={classes}
      style={columnTemplate ? { gridTemplateColumns: columnTemplate } : undefined}
      onClick={onClick}
      {...props}
    >
      {children}
    </Tag>
  );
};

const ModuleTableStatus = ({ value, children, className = "" }) => {
  const slug = statusSlug(value);
  const classes = [
    "module-table__status",
    slug ? `module-table__status--${slug}` : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return <span className={classes}>{children ?? value}</span>;
};

const ModuleTableBadge = ({ tone, children, className = "" }) => {
  const classes = [
    "module-table__badge",
    tone ? `module-table__badge--${tone}` : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return <em className={classes}>{children}</em>;
};

const ModuleTableAction = ({
  children,
  to,
  onClick,
  className = "",
  type = "button",
  stopPropagation = false,
  ...props
}) => {
  const classes = ["module-table__action", className].filter(Boolean).join(" ");

  const handleClick = (event) => {
    if (stopPropagation) event.stopPropagation();
    if (onClick) onClick(event);
  };

  const handleKeyDown = (event) => {
    if (!onClick) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      if (stopPropagation) event.stopPropagation();
      onClick(event);
    }
  };

  if (to) {
    return (
      <NavLink className={classes} to={to} onClick={handleClick} {...props}>
        {children}
      </NavLink>
    );
  }

  if (type === "text") {
    return (
      <span
        className={classes}
        role={onClick ? "button" : undefined}
        tabIndex={onClick ? 0 : undefined}
        onClick={onClick ? handleClick : undefined}
        onKeyDown={onClick ? handleKeyDown : undefined}
        {...props}
      >
        {children}
      </span>
    );
  }

  return (
    <button type={type} className={classes} onClick={handleClick} {...props}>
      {children}
    </button>
  );
};

const ModuleTablePagination = ({
  page,
  lastPage,
  onPrev,
  onNext,
  className = "",
}) => (
  <div className={["module-table__pagination", className].filter(Boolean).join(" ")}>
    <button type="button" disabled={page <= 1} onClick={onPrev}>
      Previous
    </button>
    <span>
      Page {page} of {lastPage}
    </span>
    <button type="button" disabled={page >= lastPage} onClick={onNext}>
      Next
    </button>
  </div>
);

const ModuleTableMerchantGroup = ({ children, className = "" }) => (
  <div className={["module-table__merchant-group", className].filter(Boolean).join(" ")}>
    {children}
  </div>
);

const ModuleTableMerchantRow = ({
  children,
  open = false,
  onClick,
  className = "",
  ...props
}) => {
  const classes = [
    "module-table__row",
    "module-table__row--clickable",
    "module-table__row--merchant",
    open ? "module-table__row--merchant-open" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <button
      type="button"
      className={classes}
      onClick={onClick}
      aria-expanded={open}
      {...props}
    >
      {children}
    </button>
  );
};

const ModuleTableOutletList = ({ children, className = "" }) => (
  <div className={["module-table__outlet-subrows", className].filter(Boolean).join(" ")}>
    {children}
  </div>
);

const ModuleTableOutletRow = ({ children, onClick, className = "", ...props }) => (
  <button
    type="button"
    className={["module-table__outlet-row", className].filter(Boolean).join(" ")}
    onClick={onClick}
    {...props}
  >
    {children}
  </button>
);

ModuleTable.Row = ModuleTableRow;
ModuleTable.Status = ModuleTableStatus;
ModuleTable.Badge = ModuleTableBadge;
ModuleTable.Action = ModuleTableAction;
ModuleTable.Pagination = ModuleTablePagination;
ModuleTable.MerchantGroup = ModuleTableMerchantGroup;
ModuleTable.MerchantRow = ModuleTableMerchantRow;
ModuleTable.OutletList = ModuleTableOutletList;
ModuleTable.OutletRow = ModuleTableOutletRow;

export default ModuleTable;
